import moment from 'moment';
import Mappable from './mappable';
import { standardDateStamp } from '../../utils/dates';

export default class extends Mappable {
  // createdAt: Date
  // larvalSites: LarvalSite[];
  // larvicides: Larvicides[];
  // trapSites: TrapSite[]
  // adulticides: Adulticide[]
  // landingRates: LandingRate[]
  // typesLength: int
  // isActive: bool
  // isScheduled: bool

  constructor(json) {
    super(json.id, json.name, json.coordinates);
    this.init(json);
  }

  get mappableType() {
    return 'Site';
  }

  get createdAtDateStamp() {
    return standardDateStamp(this.createdAt);
  }

  get hasMultipleTypes() {
    return this.typesLength > 1;
  }

  get type() {
    return this.types[0];
  }

  get isSingleType() {
    return this.typesLength === 1;
  }

  get types() {
    const reducer = (acc, [type, { length }]) =>
      length ? [...acc, type] : acc;

    return [
      ['Larvicide', this.larvalSites],
      ['Trap', this.trapSites],
      ['Adulticide', this.adulticides],
      ['Landing Rate', this.landingRates],
    ].reduce(reducer, []);
  }

  get badgeSrc() {
    return this.buildImage('badge');
  }

  get pinSrc() {
    return this.buildImage('pin');
  }

  get hasLarvalSites() {
    return this.larvalSites.length > 0;
  }

  get isLarvicide() {
    return this.larvalSites.length > 0;
  }

  get isTrap() {
    return this.trapSites.length > 0;
  }

  get isAdulticide() {
    return this.adulticides.length > 0;
  }

  get isLandingRates() {
    return this.landingRates.length > 0;
  }

  get latestAdulticideActivityDate() {
    return this.computeLatestActivityDateFor(this.adulticides);
  }

  get latestLarvicideActivityDate() {
    return this.computeLatestActivityDateFor(this.larvicides);
  }

  get latestAdultSurveillanceActivityDate() {
    return this.computeLatestActivityDateFor(this.adultSurveillances);
  }

  subscribe(subscribeData) {
    this.subscriptions.push(subscribeData);
  }

  publish() {
    this.subscriptions.forEach((event) => event(this));
  }

  buildImage(imageType) {
    const { isActive, isScheduled, isMandatory, isIrregular, type } = this;
    const activeState = isActive ? 'active' : 'inactive';
    const scheduledState = isScheduled ? 'scheduled' : activeState;
    const mandatoryState = !isScheduled ? '' : isMandatory ? '-mandatory' : '';
    const irregularState = isIrregular ? '-irregular' : '';

    const state = isActive
      ? `${scheduledState}${mandatoryState}${irregularState}`
      : `inactive`;

    if (!type) {
      return require(`@/assets/img/map/${imageType}-no-activity-${state}.svg`);
    }

    const typeName = this.hasMultipleTypes
      ? 'multi'
      : type.toLowerCase().replace(' ', '-');
    return require(`@/assets/img/map/${imageType}-${typeName}-${state}.svg`);
  }

  getLabelOffset() {
    const { isActive, isMandatory, isIrregular } = this;
    if (!isActive || isMandatory || isIrregular) {
      return [24, 21];
    }

    return [20, 20];
  }

  buildReminders() {
    const {
      adulticideScheduleStart,
      larvalScheduleStart,
      trapScheduleStart,
    } = this;
    const adulticideStart =
      adulticideScheduleStart &&
      standardDateStamp(new Date(adulticideScheduleStart));
    const larvalStart =
      larvalScheduleStart && standardDateStamp(moment(larvalScheduleStart));
    const trapStart =
      trapScheduleStart && standardDateStamp(moment(trapScheduleStart));
    return {
      adulticide: {
        frequency: this.adulticideScheduleFrequency,
        start: adulticideStart,
      },
      larval: {
        frequency: this.larvalScheduleFrequency,
        start: larvalStart,
      },
      trap: {
        frequency: this.trapScheduleFrequency,
        start: trapStart,
      },
    };
  }

  buildFieldNotes() {
    const reducer = (acc, site) => {
      if (!site.comments || !site.comments.length) {
        return acc;
      }

      return [...acc, ...site.comments.map(({ body }) => body)];
    };

    return {
      larval: this.larvalSites.reduce(reducer, []).join(', '),
      trap: this.trapSites.reduce(reducer, []).join(', '),
    };
  }

  addLocalSiteNote(body) {
    this.comments = [
      ...this.comments,
      {
        body,
        createdAt: new Date(),
      },
    ];
  }

  buildSiteNotes() {
    return this.comments.map(({ body, createdAt }) => ({
      body,
      createdAt: standardDateStamp(new Date(createdAt)),
    }));
  }

  computeLatestActivityDateFor(list = []) {
    if (!list.length) {
      return null;
    }

    const latest = list.reduce(({ createdAt: latest }, { createdAt }) => ({
      createdAt: latest > createdAt ? latest : createdAt,
    })).createdAt;
    return moment(latest);
  }

  hasAnyType(types) {
    const lookUp = {
      Larvicide: this.larvalSites,
      Trap: this.trapSites,
      Adulticide: this.adulticides,
      'Landing Rate': this.landingRates,
    };

    return types.some((type) => lookUp[type] && lookUp[type].length);
  }

  updateScheduled() {
    const wasScheduled = this.isScheduled;

    if (
      !this.isActive ||
      (!this.adulticideScheduleFrequency &&
        !this.larvalScheduleFrequency &&
        !this.trapScheduleFrequency)
    ) {
      this.isScheduled = false;
      this.isMandatory = false;
    } else {
      const schedulables = [
        [
          this.adulticideScheduleStart,
          this.adulticideScheduleFrequency,
          this.adulticides,
        ],
        [
          this.larvalScheduleStart,
          this.larvalScheduleFrequency,
          this.larvicides,
        ],
        [
          this.trapScheduleStart,
          this.trapScheduleFrequency,
          this.adultSurveillances,
        ],
      ];

      this.isScheduled = schedulables.some(([start, frequency, list]) => {
        if (!start || !frequency) {
          return false;
        }
        const startDate = moment(start);
        const latestActivity = this.computeLatestActivityDateFor(list);
        const latestDate =
          latestActivity && latestActivity.isAfter(startDate)
            ? latestActivity
            : startDate;
        const scheduleDate = moment(latestDate);
        const today = moment();
        const daysDifference = today.diff(scheduleDate, 'days');
        return daysDifference % frequency === 0;
      });
    }
    if (wasScheduled !== this.isScheduled) {
      this.publish();
    }
  }

  getLatestActivityDate() {
    const getLatest = (dates) =>
      dates.reduce((currentLatest, date) =>
        currentLatest.isBefore(date) ? date : currentLatest
      );
    return getLatest([
      moment(this.createdAt),
      moment(this.latestAdulticideActivityDate),
      moment(this.latestLarvicideActivityDate),
      moment(this.latestAdultSurveillanceActivityDate),
    ]);
  }

  init(data) {
    const { createdAt } = data;

    const thisOriginal = {
      ...this,
    };
    Object.assign(this, data, thisOriginal);

    this.subscriptions = [];
    this.createdAt = new Date(createdAt);
    this.typesLength = this.types.length;

    if (this.isLarvicide || this.isTrap) {
      const activitySites = [...this.larvalSites, ...this.trapSites];
      this.isActive = activitySites.some((site) => site.isActive);
      this.isMandatory = activitySites.some(({ isMandatory }) => isMandatory);
    } else {
      this.isActive = true;
      this.isMandatory = false;
    }

    this.updateScheduled();
  }
}
